import { useMemo, useCallback, useState } from "react"
import { Virtuoso } from "react-virtuoso"
import { numberFormat } from "shared/util"
import {
	Box, Stack, Slide,
	Button, IconButton,
	Checkbox, Typography, Divider,
	List, ListItem, ListItemButton, ListItemText,
} from "@mui/material"
import {
	Replay as ReplayIcon,
	Sort as SortByCountIcon,
	SortByAlpha as SortByNameIcon
} from '@mui/icons-material'

import { GridColumnDef, GridColumnDefMap, GridFilter } from "../../types"
import { isFilterd } from "../../util"
import { SearchWordField } from "components/controls/searchWordField"

type Candidate = {
	value: string
	count: number
	rate?: number
	percent?: number
}
type CandidatesFilterPros<T> = {
	field: string
	fieldDef: GridColumnDef<T>
	columnsDef: GridColumnDefMap<T>
	dataList: T[]
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	values: any[] | null
	filter: GridFilter | undefined
	filters: GridFilter[]
	setFilters: (filters: GridFilter[]) => void
}
export const CandidatesFilter = <T,>({
	field,
	fieldDef,
	columnsDef,
	dataList,
	values,
	filter,
	filters,
	setFilters
}: CandidatesFilterPros<T>) => {
	const [sortBy, setSortBy] = useState<'count' | 'name'>('count')
	const [filterText, setFilterText] = useState<string>('')

	const candidates = useMemo(
		() => {
			const map: { [index: string]: Candidate } = {}
			let totalCount = 0
			for(const record of dataList) {
				if (filters.some(filter => filter.field === field ? false : isFilterd(record, filter, columnsDef))) continue
				totalCount++
				const value = fieldDef.value(record) || '(空)'
				const arr = Array.isArray(value) ? value : [value]
				if (arr.length === 0) arr.push('(空)')
				for(const r of arr) {
					const v = (typeof r === 'string') ? r : (typeof r === 'number') ? `${r}` : null
					if (!v) continue
					const c = map[v]
					if (c) {
						c.count++
					} else {
						map[v] = { value: v, count: 1  }
					}
				}
			}
			const candidates = Object.values(map)
			const max = candidates.reduce((acc, c) => Math.max(acc, c.count), 0)
			candidates.forEach(c => {
				c.rate = max > 0 ? ((c.count * 100) / max) : 0
				c.percent = Math.ceil((c.count * 100 * 10) / totalCount) / 10
			})
			if (sortBy === 'count') {
				candidates.sort((a, b) => b.count - a.count)
			} else if (sortBy === 'name') {
				candidates.sort((a, b) => a.value.localeCompare(b.value))
			}
			if (filterText) {
				return candidates.filter(candidate => candidate.value.indexOf(filterText) >= 0)
			} else {
				return candidates
			}
		},
		[sortBy, columnsDef, field, fieldDef, dataList, filters, filterText]
	)
	const onClickCandidate = useCallback(
		(value: string) => {
			if (!field) return
			if (filter) {
				const newCandidates = !filter.candidates ? (
					[value]
				) : filter.candidates.includes(value) ? (
					filter.candidates.filter(v => v !== value)
				) : (
					filter.candidates.concat(value)
				)
				if (newCandidates.length > 0 && newCandidates.length < candidates.length) {
					// replace filter
					const newFilter: GridFilter = {
						...filter,
						label: newCandidates.join(', '),
						candidates: newCandidates
					}
					setFilters(filters.map(f => f.field === filter.field ? newFilter : f).slice())
				} else {
					// remove filter
					setFilters(filters.filter(f => f.field !== filter.field).slice())
				}
			} else {
				// new filter
				setFilters(filters.concat({
					field,
					label: value,
					candidates: [value]
				}).slice())
			}
		},
		[field, filters, setFilters, filter, candidates]
	)
	const onReset = useCallback(
		() => {
			if (!filter) return
			setFilters(filters.filter(f => f.field !== filter.field).slice())
		},
		[filters, setFilters, filter]
	)
	const isCurrent = useCallback(
		(candidate: string) => {
			const arr = values ?? []
			return (arr.length === 0) ? candidate === '(空)' : arr.includes(candidate)
		},
		[values]
	)
	return (
		<>
			<Box padding={1}>
				<SearchWordField
					fullWidth
					searchWord={filterText}
					onChangeSearchWord={setFilterText}
				/>
			</Box>
			<Divider/>
			<List disablePadding sx={{ flexGrow: 1, overflowY: 'auto', width: '100%' }}>
				<Virtuoso
					data={candidates}
					itemContent={(index, candidate) => (
						<ListItem key={`candidate-${index}-${candidate.value}`} disablePadding>
							<ListItemButton
								dense disableGutters sx={{ paddingY: 0, paddingLeft: 0, paddingRight: 0.5 }}
								onClick={() => onClickCandidate(candidate.value)}
							>
								<Checkbox
									size="small"
									checked={filter?.candidates?.includes(candidate.value) ?? true}
									tabIndex={-1}
									disableRipple
									inputProps={{ 'aria-labelledby': `check-${candidate.value}` }}
								/>
								<Box flexGrow={1} position='relative' overflow='hidden'>
									<Stack direction="row" width='100%' alignItems="center" position='relative' zIndex={1}>
										<ListItemText id={`check-${candidate.value}`} primary={candidate.value} />
										<Typography variant="caption" whiteSpace="nowrap" paddingX={1}>{numberFormat(candidate.count)}件 ({candidate.percent}%)</Typography>
									</Stack>
									<Slide direction="left" in>
										<div
											key={`bar-${candidate.value}`}
											style={{
												position: 'absolute',
												right: 0,
												top: "calc(50% - (1.75em / 2))",
												width: `${candidate.rate}%`,
												height: '1.75rem',
												background: isCurrent(candidate.value) ? 'rgb(225,225,245)' : 'rgb(235,235,235)',
												borderRadius: '4px 0 0 4px',
												transition: 'width 0.3s, background 0.3s',
												zIndex: 0
											}}
										/>
									</Slide>
								</Box>
							</ListItemButton>
						</ListItem>
					)}
				/>
			</List>
			<Divider/>
			<Stack direction="row" spacing={1} padding={1}>
				<IconButton onClick={() => setSortBy(sortBy === 'count' ? 'name' : 'count')}>
					{sortBy === 'count' ? (
						<SortByCountIcon />
					) : sortBy === 'name' ? (
						<SortByNameIcon />
					) : <></>}
				</IconButton>
				<Button
					fullWidth
					disabled={filter === undefined}
					variant="outlined"
					startIcon={<ReplayIcon />}
					sx={{ whiteSpace: 'nowrap' }}
					onClick={onReset}
				>
					初期状態に戻す
				</Button>
			</Stack>
		</>
	)
}